import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import TableIcon from './TableIcon';

const cardStyle = { backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' };
const SHOWN = 4;

// Remembered per account on this device only. A convenience, not state the
// server needs: the card also goes away for good once a table is joined.
const dismissKey = (userId) => `dspcafe:tablesPromptDismissed:${userId}`;
const readDismissed = (userId) => {
  try { return localStorage.getItem(dismissKey(userId)) === '1'; } catch { return false; }
};
const writeDismissed = (userId) => {
  try { localStorage.setItem(dismissKey(userId), '1'); } catch { /* private mode */ }
};

// Feed card for members who have joined no table yet (launch-readiness review,
// 2026-10-28): onboarding no longer asks for interests, and this is the step
// that actually changes what a new member sees. Not an onboarding step on
// purpose — one tap each, skippable. join_community_table is open to every
// signed-in member, verified or not (005).
export default function TablesPrompt({ userId }) {
  const [tables, setTables] = useState(null);
  const [joined, setJoined] = useState({});
  const [dismissed, setDismissed] = useState(() => (userId ? readDismissed(userId) : true));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!userId || dismissed) return;
    let cancelled = false;
    supabase.rpc('get_community_tables_with_membership').then(({ data, error: err }) => {
      if (cancelled) return;
      if (err) { console.error('get_community_tables_with_membership:', err); return; }
      const rows = data ?? [];
      // Already in a table → never shown. Active tables only (040).
      if (rows.some((t) => t.is_member)) { setTables([]); return; }
      // Regional tables only suit some members; topic tables first.
      const topical = rows.filter((t) => !t.regional);
      const pool = topical.length >= 2 ? topical : rows;
      setTables([...pool].sort((a, b) => (b.member_count ?? 0) - (a.member_count ?? 0)).slice(0, SHOWN));
    });
    return () => { cancelled = true; };
  }, [userId, dismissed]);

  if (dismissed || !tables || tables.length === 0) return null;

  const joinedCount = Object.values(joined).filter(Boolean).length;

  const join = async (tableId) => {
    setError('');
    setJoined((prev) => ({ ...prev, [tableId]: true }));
    const { error: err } = await supabase.rpc('join_community_table', { p_table_id: tableId });
    if (err) {
      setJoined((prev) => ({ ...prev, [tableId]: false }));
      setError('Could not join that table. Please try again.');
    }
  };

  const dismiss = () => {
    writeDismissed(userId);
    setDismissed(true);
  };

  return (
    <div className="px-4 md:px-0 pt-1 pb-2">
      <div className="rounded-2xl p-4" style={cardStyle}>
        <div className="flex items-start justify-between gap-3 mb-1">
          <h2 className="font-semibold text-base text-primary">Pick a few tables to follow</h2>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            className="p-1 -m-1 rounded-lg transition-all duration-200 hover:scale-110 active:scale-95"
            style={{ color: 'var(--text-tertiary)' }}
          >
            <X size={18} />
          </button>
        </div>
        <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
          Tables are where DSPs talk about what matters to them. Join one to start.
        </p>

        <div className="space-y-2">
          {tables.map((table) => {
            const isJoined = !!joined[table.id];
            return (
              <div
                key={table.id}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
                style={{ backgroundColor: 'var(--input-bg)' }}
              >
                <TableIcon table={table} px={36} radius="rounded-lg" />
                <Link to={`/community/${table.slug}`} className="flex-1 min-w-0 transition-opacity hover:opacity-70">
                  <p className="text-sm font-medium text-primary truncate">{table.name}</p>
                  {table.description && (
                    <p className="text-xs truncate" style={{ color: 'var(--text-tertiary)' }}>{table.description}</p>
                  )}
                </Link>
                <button
                  onClick={() => !isJoined && join(table.id)}
                  disabled={isJoined}
                  aria-label={isJoined ? `Joined ${table.name}` : `Join ${table.name}`}
                  className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all hover:brightness-110 active:scale-[0.98]"
                  style={isJoined
                    ? { color: 'var(--brand-primary)', backgroundColor: 'transparent', border: '1px solid var(--card-border)' }
                    : { color: '#fff', backgroundColor: 'var(--brand-primary)' }}
                >
                  {isJoined ? <><Check size={14} /> Joined</> : 'Join'}
                </button>
              </div>
            );
          })}
        </div>

        {error && <p className="text-xs mt-2" style={{ color: '#DC2626' }}>{error}</p>}

        <div className="flex items-center justify-between mt-3">
          <Link to="/community" className="text-sm font-semibold transition-opacity hover:opacity-70" style={{ color: 'var(--brand-primary)' }}>
            See all tables
          </Link>
          {joinedCount > 0 && (
            <button
              onClick={dismiss}
              className="text-sm font-semibold px-3 py-1.5 rounded-lg transition-all hover:brightness-110 active:scale-[0.98]"
              style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)' }}
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
